<script lang="ts" setup>
import { checkSavedCard } from '@/utils/checkSavedCard'

import { DeliveryMethods } from '@/types/api/api-v3/basket'
import { BasketPayment } from '@/types/api/api-v3/paymentMethods'

import BasketPaymentListItem from '@/components/Basket/basket-payment/BasketPaymentListItem.vue'

type Props = {
  basketPaymentMethods: BasketPayment[]
  methodDelivery: DeliveryMethods
  selectedPaymentMethod: BasketPayment | null
}

const props = defineProps<Props>()

const { isMobile, breakpoint } = useResponsive()

const emit = defineEmits(['update:selected-payment'])

const selectPaymentMethod = (paymentMethod: BasketPayment) =>
  emit('update:selected-payment', paymentMethod)

const selectedPaymentMethodType = computed(
  () => props.selectedPaymentMethod?.type,
)

const selectedSavedCardId = computed(() => {
  if (checkSavedCard(props.selectedPaymentMethod)) {
    return props.selectedPaymentMethod.id
  }

  return null
})

const checkActivePaymentItem = (paymentItem: BasketPayment) => {
  if (checkSavedCard(paymentItem)) {
    return paymentItem.id === selectedSavedCardId.value
  }

  return paymentItem.type === selectedPaymentMethodType.value
}

const paymentsList = computed(() => {
  if (!props.basketPaymentMethods) {
    return []
  }

  const list = []

  for (const payment of props.basketPaymentMethods) {
    list.push({
      payment,
      isActive: checkActivePaymentItem(payment),
    })
  }

  return list
})

const sliderOptions = computed(() => {
  if (isMobile.value) {
    return { spaceBetween: 8, slideWidth: 100 }
  }

  return { spaceBetween: 10, slideWidth: 132 }
})

const sliderKey = computed(() => {
  return `${paymentsList.value.length}-${breakpoint.value}`
})
</script>

<template>
  <div class="payment-list">
    <the-slider
      v-if="paymentsList.length"
      :key="sliderKey"
      :slide-width="sliderOptions.slideWidth"
      :space-between="sliderOptions.spaceBetween"
      :slides-to-scroll="3"
      :arrows="!isMobile"
      :snap="!isMobile"
      slide-class="payment-item"
    >
      <basket-payment-list-item
        v-for="paymentItem in paymentsList"
        :key="paymentItem.payment.type"
        :payment-item="paymentItem.payment"
        :is-active="paymentItem.isActive"
        :method-delivery="methodDelivery"
        @update:selected-payment="selectPaymentMethod"
      />
    </the-slider>
  </div>
</template>

<style lang="scss" scoped>
.payment-list {
  margin: 0 -20px;

  @include media('>=desktop-mini') {
    margin: 0;
  }

  :deep(.app-slider) {
    z-index: 3;
  }

  :deep(.app-slider__track) {
    padding: 24px 20px;
    scroll-padding-inline: 20px;

    @include media('>=desktop-mini') {
      padding: 24px 0;
      scroll-padding-inline: 0;
    }
  }
}
</style>
